import type { FeatureCollection } from 'geojson';

export const FEATURE_PALETTE = ['#1e88e5', '#e53935', '#43a047', '#fb8c00', '#8e24aa', '#00acc1', '#fdd835', '#6d4c41'];

// Mirror of `packages/utils/src/integrations/connectbase/internalId.ts`. The client
// doesn't depend on `@civilgrid/utils`, so the prefix is duplicated here. If the
// format ever changes, update both files.
export const CONNECTBASE_INTERNAL_ID_PREFIX = 'connectbase:';

export function parseConnectbaseInternalId(internalId: string | null | undefined): string | null {
  if (!internalId || !internalId.startsWith(CONNECTBASE_INTERNAL_ID_PREFIX)) return null;
  const rest = internalId.slice(CONNECTBASE_INTERNAL_ID_PREFIX.length);
  return rest.length > 0 ? rest : null;
}

// Tags each feature with its position in the source array as the top-level GeoJSON
// `id`. The Mapbox paint expression colors features by bucketing this id into the
// 8-color palette — so we don't have to inject a synthetic color property onto each
// feature, and the paint expression stays a fixed size regardless of feature count.
export function withFeatureIndex(fc: FeatureCollection): FeatureCollection {
  return {
    ...fc,
    features: fc.features.map((f, i) => ({ ...f, id: i })),
  };
}

// Builds a `match` expression that maps each feature's top-level id (its index in
// the source array) to one of the FEATURE_PALETTE colors via i % palette.length.
// Pre-bucketed so the resulting expression has at most one branch per palette
// color, no matter how many features there are.
export function buildPaletteMatchExpression(featureCount: number): unknown[] {
  const buckets: number[][] = FEATURE_PALETTE.map(() => []);
  for (let i = 0; i < featureCount; i++) {
    buckets[i % FEATURE_PALETTE.length].push(i);
  }
  const branches: unknown[] = [];
  for (let i = 0; i < FEATURE_PALETTE.length; i++) {
    if (buckets[i].length === 0) continue;
    // mapbox-gl `match` accepts either a literal or a tuple of literals as a label.
    branches.push(buckets[i].length === 1 ? buckets[i][0] : buckets[i], FEATURE_PALETTE[i]);
  }
  return ['match', ['id'], ...branches, FEATURE_PALETTE[0]];
}
